/**
 * Universal Diagram Schema
 * Supports: Architecture, User Flows, BPMN, ERD, Flowcharts, Mind Maps, Org Charts
 * AI-friendly with optional positioning and rich metadata
 */

// ============================================================================
// DIAGRAM TYPES
// ============================================================================

export type DiagramType =
  | 'architecture'  // AWS, microservices, system design
  | 'userflow'      // UX flows, user journeys
  | 'bpmn'          // Business processes, workflows
  | 'erd'           // Database schemas, entity relationships
  | 'flowchart'     // Algorithm flows, decision trees
  | 'mindmap'       // Mind maps, brainstorming
  | 'orgchart'      // Organizational charts
  | 'class'         // UML class diagrams
  | 'state'         // UML state machines / statecharts
  | 'c4'            // C4 model (context/container/component)
  | 'sequence'      // UML sequence diagrams (lifelines + messages)
  | 'gantt'         // Gantt charts (date-positioned task bars)

export type C4Level = 'c1' | 'c2' | 'c3' | 'c4'

// ============================================================================
// POSITION & SIZE
// ============================================================================

export interface Position {
  x: number
  y: number
}

export interface Size {
  width: number
  height: number
}

// ============================================================================
// BASE ELEMENT (Composite Pattern)
// ============================================================================

/**
 * Base interface for all diagram elements (nodes and containers)
 * Implements the Composite Pattern for hierarchical structures
 */
export interface BaseDiagramElement {
  id: string
  label: string
  position?: Position
  size?: Size
  icon?: string
  description?: string
  metadata?: Record<string, any>
  /**
   * Optional layer assignment (draw.io-style layers). Elements with no layerId
   * belong to the first/active layer. Round-trips through the editor.
   */
  layerId?: string
}

// ============================================================================
// STYLE
// ============================================================================

export interface NodeStyle {
  bgColor?: string
  borderColor?: string
  borderStyle?: 'solid' | 'dashed' | 'dotted'
  textColor?: string
  fontSize?: number
  fontWeight?: 'normal' | 'bold' | 'light'
  shape?:
    | 'rectangle' | 'rounded' | 'ellipse' | 'circle'
    | 'diamond' | 'hexagon' | 'triangle' | 'pentagon' | 'octagon'
    | 'parallelogram' | 'trapezoid'
    | 'cylinder' | 'cloud' | 'server' | 'storage' | 'queue'
    | 'actor' | 'person'
    | 'document' | 'note' | 'file'
}

export interface EdgeStyle {
  stroke?: string
  strokeWidth?: number
  strokeDasharray?: string
  animated?: boolean
}

// ============================================================================
// CONTAINER (Groups/Layers/Swimlanes)
// ============================================================================

export type ContainerType = 'cloud-provider' | 'layer' | 'network-zone' | 'service-group' | 'system' | 'bounded-context' | 'region' | 'swimlane' | 'pool'
export type CloudProvider = 'aws' | 'azure' | 'gcp' | 'generic'

/**
 * Container extends BaseDiagramElement
 * Containers can group nodes and other containers (Composite Pattern)
 */
export interface Container extends BaseDiagramElement {
  type?: ContainerType
  parentContainer?: string  // For container nesting support
  locked?: boolean  // If true, container cannot be dragged or modified
  autoResize?: boolean  // If true, container automatically resizes to fit children (default: false)
  style?: {
    bgColor?: string
    borderColor?: string
    borderStyle?: 'solid' | 'dashed' | 'dotted'
    textColor?: string
    opacity?: number
    headerBgColor?: string
    headerTextColor?: string
  }
  // Metadata can include provider info
  metadata?: {
    provider?: CloudProvider
    [key: string]: any
  }
}

// ============================================================================
// NODE
// ============================================================================

export type NodeType =
  // Architecture
  | 'component' | 'service' | 'database' | 'queue' | 'api' | 'storage'
  | 'function' | 'loadbalancer' | 'container' | 'cloud' | 'server'
  // User Flow
  | 'screen' | 'action' | 'actor' | 'annotation'
  // BPMN
  | 'task' | 'gateway' | 'event' | 'subprocess'
  // ERD
  | 'table' | 'view' | 'procedure'
  // Flowchart
  | 'process' | 'decision' | 'input' | 'output' | 'start' | 'end' | 'document'
  // Mind Map / Org Chart
  | 'node' | 'root' | 'branch' | 'leaf' | 'person' | 'team' | 'department'

/**
 * DiagramNode extends BaseDiagramElement
 * Represents individual components, services, actors, etc.
 */
export interface DiagramNode extends BaseDiagramElement {
  // Type
  nodeType: NodeType
  subtype?: string          // More specific: 'spa', 'microservice', 'postgres', etc.

  // Hierarchy
  parentContainer?: string  // ID of parent container
  layer?: C4Level          // For C4 model diagrams

  // Locking
  locked?: boolean  // If true, node cannot be dragged or modified

  // Rotation in degrees (0–360). Applied to the node's visual content only;
  // edges still attach to the un-rotated bounding box.
  rotation?: number

  // Stacking order (front/back). Round-trips through React Flow's node.zIndex.
  zIndex?: number

  // Shape-specific (for draw.io-style geometric shapes)
  shapeId?: string         // ID of the shape from shape registry
  shapeCategory?: string   // Category of the shape (basic, flowchart, etc.)
  isShape?: boolean        // Flag to indicate this is a shape node

  // Visual
  style?: NodeStyle

  // Content
  customData?: Record<string, any>

  // Structured Content (for technical diagrams)
  tableContent?: TableContent           // For database ERD nodes
  resourceContent?: APIResourceContent  // For REST API resource nodes
  endpointContent?: APIEndpointContent  // For API documentation nodes
  classContent?: UMLClassContent        // For UML class diagram nodes
  stateContent?: StateContent           // For UML state machine nodes
  c4Content?: C4Content                  // For C4 model nodes
  sequenceContent?: SequenceContent     // For sequence-diagram lifelines
  sequenceFragment?: SequenceFragment   // For sequence-diagram fragment boxes (alt/opt/loop/…)
  sequenceNote?: SequenceNote           // For sequence-diagram notes
  ganttContent?: GanttContent           // For gantt task bars / milestones
  ganttSection?: GanttSection           // For gantt section bands
  ganttAxis?: GanttAxis                 // For the gantt date-axis ruler
}

/**
 * SINGLE SOURCE OF TRUTH for the structured-content data fields a node carries.
 *
 * These MUST round-trip identically through parse (DiagramData → React Flow data),
 * serialize (React Flow data → DiagramData), and the collab flush — otherwise the
 * node re-parses to a bare label (the "Gantt gone after tab switch" bug class).
 * The parser pass-through AND the serializer both iterate THIS list, and a test
 * (serializer.roundtrip) iterates it too, so adding a new structured-content type
 * requires touching exactly one place and is automatically round-trip-guarded.
 *
 * When you add a new `xContent` field to DiagramNode above, add its key here.
 */
export const STRUCTURED_CONTENT_KEYS = [
  'tableContent',
  'resourceContent',
  'endpointContent',
  'classContent',
  'stateContent',
  'c4Content',
  'sequenceContent',
  'sequenceFragment',
  'sequenceNote',
  'ganttContent',
  'ganttSection',
  'ganttAxis',
] as const

export type StructuredContentKey = (typeof STRUCTURED_CONTENT_KEYS)[number]

// ============================================================================
// STRUCTURED CONTENT (Technical Diagram Nodes)
// ============================================================================

/**
 * Database Table Content (ERD Diagrams)
 * Renders as table with columns, data types, and constraints
 */
export interface TableContent {
  tableName: string
  columns: Array<{
    name: string
    dataType: string              // e.g., "VARCHAR(255)", "INT", "UUID"
    isPrimaryKey?: boolean         // Show PK badge
    isForeignKey?: boolean         // Show FK badge
    isNotNull?: boolean           // NOT NULL constraint
    isUnique?: boolean            // UNIQUE constraint
    defaultValue?: string         // Default value
    description?: string          // Column description
  }>
  indexes?: Array<{
    name: string
    columns: string[]
    type?: 'BTREE' | 'HASH' | 'FULLTEXT'
  }>
  foreignKeys?: Array<{
    column: string
    references: {
      table: string
      column: string
    }
    onDelete?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
    onUpdate?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
  }>
}

/**
 * REST API Resource Content
 * Renders as UML-style resource with stereotype, attributes, and methods
 */
export interface APIResourceContent {
  stereotype: string                    // e.g., "resource", "controller", "service"
  resourceName: string                  // e.g., "users", "products"
  attributes?: string[]                 // Resource fields (id, username, email)
  methods: Array<{
    type: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
    signature: string                   // e.g., "GET()", "POST(body)"
    description?: string
  }>
}

/**
 * UML Class Content (Class Diagrams)
 * Renders as a compartmented box: name (+ optional «stereotype»/generics),
 * an attributes compartment, and a methods compartment. Members carry an
 * optional UML visibility prefix (+ public, - private, # protected, ~ package)
 * so the model round-trips cleanly with Mermaid `classDiagram` text.
 *
 * Relationships between classes are expressed as ordinary DiagramEdges whose
 * arrow markers encode the UML relation (e.g. hollow-triangle = inheritance,
 * filled-diamond = composition) via `edge.data.arrowStart/arrowEnd`.
 */
export interface UMLClassContent {
  className: string
  stereotype?: string                   // e.g. "interface", "abstract", "enumeration"
  generics?: string                     // e.g. "<T>", "<K, V>"
  attributes?: Array<{
    visibility?: '+' | '-' | '#' | '~'  // public / private / protected / package
    name: string
    type?: string                       // e.g. "string", "List<User>"
    isStatic?: boolean
  }>
  methods?: Array<{
    visibility?: '+' | '-' | '#' | '~'
    signature: string                   // e.g. "getName(): string", "save(user: User)"
    isAbstract?: boolean
    isStatic?: boolean
  }>
}

/**
 * UML State Machine Content (Statecharts)
 * A state node renders by `kind`:
 *   - 'simple'/'composite' : rounded stadium box with the state name, optional
 *     description and internal transitions (entry/exit/do actions).
 *   - 'initial'            : small filled dot
 *   - 'final'              : ringed dot
 *   - 'choice'             : diamond
 *   - 'fork'/'join'        : thick bar
 * Composite states nest children via the existing Container/parentContainer
 * mechanism. Transitions are ordinary DiagramEdges labelled
 * `event [guard] / action` (per UML/Mermaid stateDiagram convention).
 */
export interface StateContent {
  stateName: string
  kind?: 'simple' | 'composite' | 'initial' | 'final' | 'choice' | 'fork' | 'join'
  description?: string
  /** Internal actions, e.g. "entry / openDoor", "do / poll", "exit / cleanup". */
  internalTransitions?: string[]
}

/**
 * C4 Model Content (Context / Container / Component diagrams)
 * Renders as a labelled box styled by `elementType`: person (actor head + body),
 * system / container / component (rounded box with an element-type chip, the
 * name, an optional [technology] line, and a description). The `external` flag
 * greys the element (outside the system boundary). Boundaries (system /
 * bounded-context) reuse the existing Container/parentContainer mechanism.
 * Relationships are ordinary labelled DiagramEdges (e.g. "Uses [HTTPS]").
 */
export interface C4Content {
  elementType: 'person' | 'system' | 'container' | 'component'
  name: string
  technology?: string                   // e.g. "Spring Boot", "PostgreSQL"
  description?: string
  external?: boolean                    // outside the system-in-focus boundary
}

/**
 * Sequence Diagram Content
 *
 * A sequence diagram is laid out deterministically (time flows top→down as
 * discrete rows; participants are fixed left→right columns), NOT via dagre.
 * Three node kinds, selected by which field is present:
 *   - `sequenceContent`  : a participant LIFELINE (header + dashed line + per-row
 *                          connection handles + activation bars).
 *   - `sequenceFragment` : an alt/opt/loop/… box spanning columns × rows.
 *   - `sequenceNote`     : a note over/left/right of participant(s).
 * Messages are ordinary DiagramEdges pinned to row handles (`seq-<row>-<side>`).
 */
export interface SequenceContent {
  participantName: string
  kind?: 'actor' | 'participant'
  /** Total event rows in the diagram — the lifeline renders a handle per row/side. */
  rowCount: number
  /** Activation bars (nestable), as [startRow, endRow] spans with a nesting depth. */
  activations?: Array<{ startRow: number; endRow: number; depth?: number }>
}

export interface SequenceFragment {
  kind: 'alt' | 'opt' | 'loop' | 'par' | 'critical' | 'break'
  label?: string                        // e.g. "[balance > 0]"
  /** else / and dividers within the fragment, at the given row. */
  sections?: Array<{ label?: string; atRow: number }>
}

export interface SequenceNote {
  text: string
  placement: 'over' | 'left' | 'right'
}

/**
 * Gantt Chart Content
 *
 * Laid out deterministically: tasks are date-positioned horizontal bars (one per
 * row), grouped into sections, with a date-axis ruler, milestones (diamonds),
 * and finish-to-start dependency edges. Three node kinds by which field is present:
 *   - `ganttContent` : a task BAR (or milestone diamond when isMilestone).
 *   - `ganttSection` : a section band + left label spanning its task rows.
 *   - `ganttAxis`    : the top date ruler (one wide node) with precomputed ticks.
 * Dependencies are ordinary DiagramEdges between task nodes.
 */
export interface GanttContent {
  taskName: string
  startISO: string                      // resolved start date (YYYY-MM-DD)
  endISO: string                        // resolved end date (exclusive)
  progress?: number                     // 0–100 completion
  isMilestone?: boolean
  status?: 'done' | 'active' | 'crit' | 'normal'
  section?: string
}

export interface GanttSection {
  name: string
  index: number                         // 0-based section order (for alternating bands)
}

export interface GanttAxis {
  startISO: string
  days: number                          // total day span of the chart
  dayWidth: number                      // px per day
  ticks: Array<{ label: string; dayOffset: number; major?: boolean }>
}

/**
 * API Endpoint Documentation Content
 * Renders as API endpoint list with methods, paths, and descriptions
 */
export interface APIEndpointContent {
  entityName: string                    // e.g., "MEMBER", "BOOK"
  description: string                   // Entity description
  endpoints: Array<{
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
    items: Array<{
      path: string                      // e.g., "/users/{id}", "/users?q={searchTerm}"
      description: string               // What this endpoint does
      parameters?: Array<{
        name: string
        type: string
        required: boolean
        description?: string
      }>
      requestExample?: string           // JSON example of request body
      responseExample?: string          // JSON example of response body
      responses?: Record<number, string>  // Status codes and descriptions
    }>
  }>
}

// ============================================================================
// EDGE (Connections)
// ============================================================================

export type EdgeType =
  // Architecture
  | 'http' | 'grpc' | 'websocket' | 'message-queue' | 'data-flow'
  // User Flow
  | 'navigation' | 'gesture' | 'condition'
  // BPMN
  | 'sequence' | 'message' | 'association'
  // ERD
  | 'one-to-one' | 'one-to-many' | 'many-to-many'
  // Flowchart
  | 'flow' | 'yes' | 'no'
  // General
  | 'dependency' | 'relationship' | 'link'

export type ReactFlowEdgeType = 'smoothstep' | 'step' | 'straight' | 'bezier' | 'default' | 'custom' | 'smart'

export interface DiagramEdge {
  id?: string              // Optional - will be auto-generated if not provided
  source: string           // Source node ID
  target: string           // Target node ID
  sourceHandle?: string    // Specific handle on source node
  targetHandle?: string    // Specific handle on target node

  // Layer assignment (draw.io-style layers); edges with no layerId belong to
  // the first/active layer.
  layerId?: string

  label?: string

  // Type
  edgeType?: EdgeType
  type?: ReactFlowEdgeType // React Flow edge type

  // Metadata (AI can add rich details!)
  protocol?: string        // REST, GraphQL, gRPC, WebSocket, etc.
  method?: string          // GET, POST, etc.
  description?: string

  // Visual
  animated?: boolean
  style?: EdgeStyle

  // Edge data for advanced customization (offset, styling, labels, etc.)
  data?: {
    offset?: number                    // Path offset for parallel/bidirectional edges (pixels)
    edgeDirection?: 'forward' | 'backward' | 'single'  // Direction in bidirectional pair
    bidirectional?: boolean            // Whether this edge is bidirectional
    lineStyle?: 'solid' | 'dashed' | 'dotted' | 'longdash'
    lineWidth?: number
    lineCap?: 'butt' | 'round' | 'square'
    lineJoin?: 'miter' | 'round' | 'bevel'
    color?: string
    opacity?: number
    arrowStart?: string                // Arrow type at start
    arrowEnd?: string                  // Arrow type at end
    arrowSize?: number                 // Arrow size multiplier
    sourceOrientation?: 'auto' | 'top' | 'bottom' | 'left' | 'right'
    targetOrientation?: 'auto' | 'top' | 'bottom' | 'left' | 'right'
  handlePreference?: 'auto' | 'vertical' | 'horizontal'
    // When true, the edge is pinned to its assigned 12-handle (React Flow handle
    // positions) instead of the default floating boundary attachment.
    fixedConnection?: boolean
    portSeparated?: boolean            // Set when handle-assignment gave this edge a distinct perimeter port (offset pass then locks its curve offset to 0)
    labelFontSize?: number
    labelBackground?: string
    labelBackgroundColor?: string
    labelZIndex?: number
    labelPosition?: number             // 0.0 to 1.0, position along path
    labelMode?: 'default' | 'split' | 'markdown'
    labelText?: string
    shadowEnabled?: boolean
    shadowColor?: string
    shadowBlur?: number
    shadowOffsetX?: number
    shadowOffsetY?: number
    zIndex?: number
    [key: string]: any                 // Allow additional custom properties
  }

  metadata?: Record<string, any>
}

// ============================================================================
// LAYOUT
// ============================================================================

export type LayoutAlgorithm =
  | 'dagre'      // Directed graph layout (best for flowcharts, architecture)
  | 'elk'        // Eclipse Layout Kernel (complex layouts)
  | 'force'      // Force-directed (organic, mind maps)
  | 'manual'     // No auto-layout

export type LayoutDirection = 'LR' | 'TB' | 'RL' | 'BT' // Left-Right, Top-Bottom, etc.

export interface LayoutConfig {
  algorithm?: LayoutAlgorithm
  direction?: LayoutDirection
  spacing?: {
    node?: number    // Space between nodes
    rank?: number    // Space between ranks/layers
    edge?: number    // Edge separation
  }
}

// ============================================================================
// LAYERS (draw.io-style)
// ============================================================================

/**
 * A diagram layer. Elements (nodes/edges) reference a layer via `layerId`.
 * `visible` controls whether the layer's elements are rendered; `locked`
 * controls whether they can be selected/dragged.
 */
export interface DiagramLayer {
  id: string
  name: string
  visible: boolean
  locked: boolean
}

// ============================================================================
// COMMENTS / ANNOTATION THREADS
// ============================================================================

/**
 * A single reply within a comment thread.
 */
export interface DiagramCommentReply {
  id: string
  author: string
  text: string
  /** ISO-8601 timestamp; optional so older/AI-authored data round-trips. */
  createdAt?: string
}

/**
 * A comment / annotation thread anchored to the canvas. Either pinned to a
 * specific node (via `nodeId`) or to a free-floating canvas point (via
 * `anchor` in flow coordinates). At least one anchor form should be present;
 * when both are given `nodeId` wins for rendering and `anchor` acts as a
 * relative offset hint.
 *
 * Fully optional on `DiagramData` and additive — documents without comments
 * round-trip byte-identically.
 */
export interface DiagramComment {
  id: string
  /** Free-floating anchor point in flow coordinates. */
  anchor?: Position
  /** Node this thread is pinned to (takes precedence over `anchor`). */
  nodeId?: string
  author: string
  text: string
  /** Whether the thread has been marked resolved. */
  resolved?: boolean
  /** ISO-8601 timestamp; optional so older/AI-authored data round-trips. */
  createdAt?: string
  /** Threaded replies (oldest first). */
  replies?: DiagramCommentReply[]
}

// ============================================================================
// MAIN DIAGRAM SCHEMA
// ============================================================================

export interface DiagramData {
  // Meta
  diagramType: DiagramType
  title?: string
  description?: string
  level?: C4Level

  metadata?: {
    author?: string
    created?: string
    version?: string
    tags?: string[]
    [key: string]: any
  }

  // Content
  containers?: Container[]
  nodes: DiagramNode[]
  edges?: DiagramEdge[]

  // Layers (draw.io-style). Optional — when absent a single default layer is
  // assumed and all elements belong to it.
  layers?: DiagramLayer[]

  // Comments / annotation threads (anchored, resolvable). Optional and additive
  // — documents without comments round-trip unchanged.
  comments?: DiagramComment[]

  // Layout
  layout?: LayoutConfig

  // Viewport (optional - for saving view state)
  viewport?: {
    x?: number
    y?: number
    zoom?: number
  }

  // Editor grid (snap-to-grid). Both optional and additive — when absent the
  // editor defaults to snapToGrid off and a gridSize of 16.
  snapToGrid?: boolean
  gridSize?: number
}

// ============================================================================
// VALIDATION
// ============================================================================

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
  warnings: ValidationWarning[]
}

export interface ValidationError {
  type: 'error'
  message: string
  path?: string
  code?: string
}

export interface ValidationWarning {
  type: 'warning'
  message: string
  path?: string
  code?: string
}
